import { useState, useEffect, useRef, useCallback } from 'react'
import { api } from '../lib/api'
import { useStore } from '../store/useStore'

interface UseQuestionRevealReturn {
  availableQuestionsCount: number
  availableQuestions: any[]
  revealingQuestion: boolean
  showGiftAnimation: boolean
  handleRevealQuestion: () => Promise<void>
}

export function useQuestionReveal(
  matchId: string | undefined,
  messagesLength: number,
  isAiChatting: boolean,
  showAlert: (msg: string) => void,
  fetchMessagesQuietly: () => Promise<void>
): UseQuestionRevealReturn {
  const { fetchPoints } = useStore()
  const [availableQuestionsCount, setAvailableQuestionsCount] = useState(0)
  const [availableQuestions, setAvailableQuestions] = useState<any[]>([])
  const [revealingQuestion, setRevealingQuestion] = useState(false)
  const [showGiftAnimation, setShowGiftAnimation] = useState(false)
  const prevQuestionsCountRef = useRef(0)

  // Load available questions
  useEffect(() => {
    const loadAvailableQuestions = async () => {
      if (!matchId) return

      try {
        const data = await api.matches.getAvailableQuestions(parseInt(matchId))
        const newCount = data.questions?.length || 0

        if (newCount > prevQuestionsCountRef.current && prevQuestionsCountRef.current !== 0) {
          setShowGiftAnimation(true)
          setTimeout(() => setShowGiftAnimation(false), 2000)
        }

        prevQuestionsCountRef.current = newCount
        setAvailableQuestions(data.questions || [])
        setAvailableQuestionsCount(newCount)
      } catch {
        // Silent fail
      }
    }

    loadAvailableQuestions()
  }, [matchId, messagesLength])

  const handleRevealQuestion = useCallback(async () => {
    if (revealingQuestion || !matchId) return

    if (isAiChatting) {
      showAlert('상대가 온라인일 때 질문을 공개할 수 있습니다.')
      return
    }

    if (availableQuestions.length === 0) {
      showAlert('아직 공개할 수 있는 질문이 없습니다. 대화를 더 나눠보세요!')
      return
    }

    setRevealingQuestion(true)
    try {
      const questionToReveal = availableQuestions[0]
      const result = await api.matches.revealQuestion(parseInt(matchId), questionToReveal.question_id)

      if (result.success) {
        showAlert('질문이 공개되었습니다! (-1 GP)')
        await fetchMessagesQuietly()
        await fetchPoints()

        const data = await api.matches.getAvailableQuestions(parseInt(matchId))
        setAvailableQuestions(data.questions || [])
        setAvailableQuestionsCount(data.questions?.length || 0)
      }
    } catch (error: any) {
      if (error.message?.includes('포인트가 부족합니다')) {
        showAlert('포인트가 부족합니다. (1 GP 필요)')
      } else if (error.error === 'Question not available for reveal') {
        showAlert('아직 공개할 수 있는 질문이 없습니다. 대화를 더 나눠보세요!')
      } else {
        showAlert(error.message || '질문 공개에 실패했습니다.')
      }
      const data = await api.matches.getAvailableQuestions(parseInt(matchId))
      setAvailableQuestions(data.questions || [])
      setAvailableQuestionsCount(data.questions?.length || 0)
    } finally {
      setRevealingQuestion(false)
    }
  }, [revealingQuestion, matchId, isAiChatting, availableQuestions, showAlert, fetchMessagesQuietly, fetchPoints])

  return {
    availableQuestionsCount,
    availableQuestions,
    revealingQuestion,
    showGiftAnimation,
    handleRevealQuestion,
  }
}
